import { saveSearchHistory } from './api';

// Searches made while anonymous. They are kept in this browser's localStorage
// until the user signs up or logs in, and then flushed into their profile's
// `metadata.recentSearches` through the same endpoint the logged-in searches
// use. localStorage and not a cookie: the server never needs them per request.
const STORAGE_KEY = 'cocopool.pendingSearches';
const MAX_SEARCHES = 10;
// Older searches are dropped so a shared computer doesn't hand someone else's
// history to whoever registers on it weeks later.
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    // Blocked storage (Safari private mode, disabled site data) throws on access.
    return null;
  }
};

export const readPendingSearches = (now = Date.now()) => {
  try {
    const parsed = JSON.parse(storage()?.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(s => {
      const searchedAt = Date.parse(s?.searchedAt);
      return Number.isFinite(searchedAt) && now - searchedAt <= MAX_AGE_MS;
    });
  } catch (e) {
    return [];
  }
};

export const addPendingSearch = search => {
  try {
    const searches = [search, ...readPendingSearches()].slice(0, MAX_SEARCHES);
    storage()?.setItem(STORAGE_KEY, JSON.stringify(searches));
  } catch (e) {
    // Best-effort: a full or blocked storage just loses the search, as before.
  }
};

export const clearPendingSearches = () => {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch (e) {
    // Ignore — see addPendingSearch.
  }
};

// Send the anonymous history to the now-authenticated user's profile. Only call
// it with a session: the endpoint resolves the user from it. The local copy is
// cleared only after the server accepted it, so a failed flush is retried on the
// next boot or login. Never rejects.
export const flushPendingSearches = () => {
  const searches = readPendingSearches();
  if (searches.length === 0) {
    clearPendingSearches();
    return Promise.resolve();
  }
  return saveSearchHistory({ searches })
    .then(() => clearPendingSearches())
    .catch(() => {});
};
